// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { object, func, bool, string, oneOfType, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { uniqueId } from 'lodash';
import {
  Button,
  Loading,
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';

import { NWTextInput, NWCheckbox, NWNotification } from 'Components/shared';

const StyledAccountPersonalData = styled.div`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .sectionBody {
      display: flex;
      gap: 16px;
      padding: 16px;

      &.column {
        flex-direction: column;
      }

      &.consent {
        > div > div {
          margin: 0;
        }
      }

      &.requestData {
        flex-direction: column;
        
        .exportActions {
          display: flex;
          gap: 16px;

          button {
            width: 100%;
            max-width: 230px;
            height: 40px !important;
            border: 1px solid #1F59A2;
            color: #1F59A2;
          }
        }
      }

      &.skeleton {
        flex-direction: column;
        
        > div {
          flex: auto;
        }
      }

      > div {
        flex: 1;
      }
    }
  }

  .submit {
    display: flex;
    justify-content: end;

    button {
      width: 100%;
      max-width: 255px;
      height: 40px !important;
      background-color: #da1e28;
    }
  }

  button svg {
    height: 16px;
    margin-right: 16px;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .sectionBody {
      flex-direction: column;
    }

    section {
      .sectionBody {
        &.requestData {
          .exportActions {
            flex-direction: column;

            button {
              width: 100%;
              max-width: none;
            }
          }
        }
      }
    }

    .submit {
      button {
        width: 100%;
        max-width: none;
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 100%;
`;

const fields = {
  firstName: 'firstName',
  lastName: 'lastName',
  operationalConsent: 'operationalConsent',
  marketingConsent: 'marketingConsent',
  createdOn: 'createdOn',
};

const AccountPersonalData = ({
  loading,
  validators,
  register,
  rhfErrors,
  operationalNotification,
  marketingNotification,
  handlePersonalData,
  personalDataLoading,
  handleOnSubmit,
  submitLoading,
  operationalConsentValue,
  handleConsent,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  return (
    <StyledAccountPersonalData theme={ theme }>
      { loading ? (
        <>
          <section>
            <h4><SkeletonText /></h4>
            <div className="sectionBody skeleton">
              { Array.from({ length: 3 }, () => <StyledSkeletonPlaceholder key={ uniqueId('skeletonPlaceholder') } />)}
            </div>
          </section>
          { Array.from({ length: 2 }, () => (
            <section key={ uniqueId('skeletonPlaceholder') }>
              <h4><SkeletonText /></h4>
              <div className="sectionBody skeleton">
                <SkeletonText paragraph />
              </div>
            </section>
          ))}
        </>
      ) : (
        <>
          <section>
            <h4>{ t('profile.personalInformation') }</h4>
            <div className="sectionBody">
              <div>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.firstName }`) }
                  { ...sharedProps(fields.firstName) }
                />
              </div>
              <div>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.lastName }`) }
                  { ...sharedProps(fields.lastName) }
                />
              </div>
            </div>
          </section>
          <section>
            <h4>{ t('profile.consentProcessingPolicy') }</h4>
            <div className="sectionBody column consent">
              <div>
                <NWCheckbox
                  name={ t('profile.operationalPurpose') }
                  labelText={ t(`profile.${ fields.operationalConsent }`) }
                  { ...sharedProps(fields.operationalConsent) }
                  onChange={ value => handleConsent(fields.operationalConsent, value) }
                />
                <NWNotification errors={ operationalNotification } />
              </div>
              <div>
                <NWCheckbox
                  name={ t('profile.marketingPurpose') }
                  labelText={ t(`profile.${ fields.marketingConsent }`) }
                  disabled={ !operationalConsentValue }
                  { ...sharedProps(fields.marketingConsent) }
                  onChange={ value => handleConsent(fields.marketingConsent, value) }
                />
                <NWNotification errors={ marketingNotification } />
              </div>
              <NWTextInput
                light
                required
                readOnly
                labelText={ t(`profile.${ fields.createdOn }`) }
                { ...sharedProps(fields.createdOn) }
              />
            </div>
          </section>
          <section>
            <h4>{ t('profile.personalDataRequest') }</h4>
            <div className="sectionBody column requestData">
              <p>{ t('profile.referTo') }: <Link to="/information/data-processing-policy">{ t('profile.personalDataProcessingPolicy') }</Link></p>
              <p>{ t('profile.makeRequestFor') }:</p>
              <div className="exportActions">
                <Button
                  className="primary"
                  kind="ghost"
                  onClick={ () => handlePersonalData('POST') }
                >
                  { (personalDataLoading === 'POST') && <Loading active small withOverlay={ false } /> }
                  { t('profile.exportPersonalData') }
                </Button>
                <Button
                  className="primary"
                  kind="ghost"
                  onClick={ () => handlePersonalData('DELETE') }
                >
                  { (personalDataLoading === 'DELETE') && <Loading active small withOverlay={ false } /> }
                  { t('profile.deletePersonalData') }
                </Button>
              </div>
            </div>
          </section>
        </>
      )}
      <div className="submit">
        <Button
          className="primary"
          type="submit"
          disabled={ loading }
          onClick={ handleOnSubmit }
        >
          { submitLoading && <Loading active small withOverlay={ false } /> }
          { t('profile.saveProfile') }
        </Button>
      </div>
    </StyledAccountPersonalData>
  );
};

AccountPersonalData.propTypes = {
  loading: bool,
  validators: object,
  register: func,
  rhfErrors: object,
  operationalNotification: array,
  marketingNotification: array,
  handlePersonalData: func,
  personalDataLoading: oneOfType([bool, string]),
  handleOnSubmit: func,
  submitLoading: bool,
  operationalConsentValue: bool,
  handleConsent: func,
};

AccountPersonalData.displayName = 'AccountPersonalDataComponent';

export default AccountPersonalData;
